import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createCreditsCheckout,
  getCreditsBalance,
  getCreditsHistory,
  type AICreditLedgerEntry,
  type CreditPack,
  type CreditsBalanceResponse,
} from '../api/client';
import PageToolbar from '../components/PageToolbar';

const PAGE_SIZE = 20;

function ledgerTypeLabel(t: (key: string) => string, type: string, opType?: string): string {
  const typeKey = `credits.type.${type}`;
  const typeLabel = t(typeKey);
  const resolvedType = typeLabel === typeKey ? type : typeLabel;
  if (!opType) return resolvedType;
  const opKey = `credits.op.${opType}`;
  const opLabel = t(opKey);
  const resolvedOp = opLabel === opKey ? opType : opLabel;
  if (type === 'spend') return resolvedOp;
  return `${resolvedType} · ${resolvedOp}`;
}

const CreditsPage: React.FC = () => {
  const { t } = useTranslation();
  const [balance, setBalance] = useState<CreditsBalanceResponse | null>(null);
  const [history, setHistory] = useState<AICreditLedgerEntry[]>([]);
  const [cursorBefore, setCursorBefore] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buying, setBuying] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bal, hist] = await Promise.all([getCreditsBalance(), getCreditsHistory({ limit: PAGE_SIZE })]);
      setBalance(bal);
      const items = hist.items ?? [];
      setHistory(items);
      setCursorBefore(items.length > 0 ? items[items.length - 1]!.createdAt : null);
      setHasMore(items.length === PAGE_SIZE);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('credits.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading) return;
    if (!hasMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const hist = await getCreditsHistory({
        before: cursorBefore ?? undefined,
        limit: PAGE_SIZE,
      });
      const newItems = hist.items ?? [];
      if (newItems.length === 0) {
        setHasMore(false);
        return;
      }
      setHistory((prev) => [...prev, ...newItems]);
      setCursorBefore(newItems[newItems.length - 1]!.createdAt);
      setHasMore(newItems.length === PAGE_SIZE);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('credits.loadError'));
    } finally {
      setLoadingMore(false);
    }
  }, [cursorBefore, hasMore, loading, loadingMore, t]);

  const buyPack = async (pack: CreditPack) => {
    setBuying(pack.id);
    try {
      const url = await createCreditsCheckout(pack.id);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('credits.checkoutError'));
      setBuying(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageToolbar standalone>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{t('credits.title')}</h1>
      </PageToolbar>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('credits.balance')}</p>
        <p className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white tabular-nums">
          {loading ? '…' : (balance?.balance ?? 0).toLocaleString()}
        </p>
        {balance?.grantedThisMonth && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('credits.grantedThisMonth')}</p>
        )}
      </div>

      {(balance?.packs?.length ?? 0) > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('credits.buyCredits')}</h2>
          <div className="flex flex-wrap gap-3">
            {balance!.packs.map((pack) => (
              <button
                key={pack.id}
                type="button"
                disabled={buying !== null}
                onClick={() => void buyPack(pack)}
                className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50"
              >
                {buying === pack.id
                  ? t('credits.redirecting')
                  : t('credits.buyPack', { amount: pack.credits.toLocaleString() })}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('credits.history')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{t('credits.colTime')}</th>
                <th className="px-4 py-3">{t('credits.colType')}</th>
                <th className="px-4 py-3">{t('credits.colAmount')}</th>
                <th className="px-4 py-3">{t('credits.colIp')}</th>
                <th className="px-4 py-3">{t('credits.colUa')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    …
                  </td>
                </tr>
              )}
              {!loading && history.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    {t('credits.emptyHistory')}
                  </td>
                </tr>
              )}
              {history.map((row) => (
                <tr key={row.id} className="text-gray-700 dark:text-gray-300">
                  <td className="px-4 py-2 whitespace-nowrap">{new Date(row.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-2">
                    {ledgerTypeLabel(t, row.type, row.opType)}
                  </td>
                  <td className={`px-4 py-2 tabular-nums ${row.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                    {row.amount > 0 ? '+' : ''}
                    {row.amount.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{row.clientIp}</td>
                  <td className="px-4 py-2 max-w-[200px] truncate text-xs" title={row.userAgent}>
                    {row.userAgent}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && history.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center">
              <button
                type="button"
                className="btn btn-secondary px-4 py-2 text-sm disabled:opacity-50"
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {loadingMore ? t('credits.loadingMore') : t('credits.loadMore')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditsPage;
